import React, { useEffect } from 'react'

import { Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { createPortal } from 'react-dom'

type Drawer = {
  title: string
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  toggleButtonClasses?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  confirmBeforeClose?: boolean
  confirmBeforeCloseMessage?: string
  onBeforeClose?: () => void
  children: React.ReactNode
  extraClose?: () => void
}

/** A Drawer component that slides in from the right of the screen */
export function Drawer({
  title,
  isOpen,
  setIsOpen,
  size = 'md',
  confirmBeforeClose = false,
  confirmBeforeCloseMessage = 'Are you sure you want to exit?',
  onBeforeClose,
  children,
  extraClose,
}: Drawer) {
  const drawerSize = classNames(
    'fixed z-[50] top-0 bottom-0 right-0 bg-base-100 shadow-lg overflow-auto focus:outline-none',
    {
      'w-1/5': size === 'xs',
      'w-1/3': size === 'sm',
      'w-[576px]': size === 'md',
      'w-4/6': size === 'lg',
      'w-5/6': size === 'xl',
    }
  )

  const maybeClose = () => {
    if (confirmBeforeClose) {
      const confirm = window.confirm(confirmBeforeCloseMessage)
      onBeforeClose?.()
      if (confirm) {
        setIsOpen(false)
        setTimeout(() => {
          extraClose?.()
        }, 300)
      }
    } else {
      onBeforeClose?.()
      setIsOpen(false)
      setTimeout(() => {
        extraClose?.()
      }, 300)
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        maybeClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  if (typeof window === 'undefined') return null

  return (
    <>
      {createPortal(
        <Transition
          show={isOpen}
          unmount
        >
          {/* Overlay */}
          <div
            className='fixed z-20 inset-0 bg-black opacity-30'
            onClick={() => maybeClose()}
          />

          {/* Sidebar */}
          <Transition.Child
            unmount
            enter='transition ease-in-out duration-300'
            enterFrom='translate translate-x-full'
            enterTo='translate translate-x-0 focus:outline'
            leave='transition ease-in-out duration-300'
            leaveFrom='translate translate-x-0'
            leaveTo='translate translate-x-full'
            className={drawerSize}
          >
            <div className='m-5'>
              <div className='flex'>
                <div className='text-xl text-gray-700 font-semibold grow'>{title}</div>
                <button
                  type='button'
                  className='btn btn-ghost btn-sm cursor-pointer'
                  onClick={() => maybeClose()}
                >
                  <XMarkIcon
                    height={22}
                    width={22}
                    className='float-right'
                  />
                </button>
              </div>
              <div className='mt-3'>{children}</div>
            </div>
          </Transition.Child>
        </Transition>,
        document.body
      )}
    </>
  )
}
